import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Copy, FileText, ChevronDown } from "lucide-react";
import { SEVERITY, fmt, copyText } from "./shared";

export function IncidentDialog({ alertId, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!alertId) return;
    setD(null); setErr(false);
    api.get(`/superadmin/watchtower/incidents/${alertId}`).then((r) => setD(r.data)).catch(() => setErr(true));
  }, [alertId]);
  const inc = d?.incident;
  return (
    <Dialog open={!!alertId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="wt-incident-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl flex items-center gap-2 flex-wrap" data-testid="wt-incident-title">
            {inc ? inc.title : err ? "Unable to load incident" : "Loading…"}
            {inc && <Badge className={inc.still_active ? SEVERITY[inc.severity] : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"} data-testid="wt-incident-status">{inc.still_active ? inc.severity_word : "Resolved"}</Badge>}
          </DialogTitle>
          <DialogDescription>{d ? `${d.system.name}${d.system.is_test ? " (test system)" : ""}` : ""}</DialogDescription>
        </DialogHeader>
        {inc && (
          <div className="space-y-4 text-sm">
            <p data-testid="wt-incident-explanation">{inc.explanation}</p>
            {inc.means && <p className="text-muted-foreground"><span className="font-semibold text-foreground">What this means: </span>{inc.means}</p>}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:text-sm">
              <dt className="text-muted-foreground">Started</dt><dd data-testid="wt-incident-started">{fmt(inc.started_at)}</dd>
              <dt className="text-muted-foreground">Last seen</dt><dd>{fmt(inc.last_seen_at)}</dd>
              {inc.resolved_at && <><dt className="text-muted-foreground">Resolved</dt><dd data-testid="wt-incident-resolved">{fmt(inc.resolved_at)}</dd></>}
              <dt className="text-muted-foreground">{inc.still_active ? "Active for" : "Total duration"}</dt><dd>{inc.duration_text}</dd>
              <dt className="text-muted-foreground">Times detected</dt><dd>{inc.occurrences}</dd>
              <dt className="text-muted-foreground">Still happening</dt><dd>{inc.still_active ? "Yes" : "No — recovered"}</dd>
              <dt className="text-muted-foreground">Monitoring</dt><dd>{d.system.freshness?.state === "current" ? "Current" : d.system.freshness?.state === "never" ? "Never checked" : "Health data may be stale"}</dd>
            </dl>
            {inc.still_active && (
              <div className="rounded-lg border bg-muted/40 p-3" data-testid="wt-incident-guidance">
                <p className="font-semibold mb-1">What should I do?</p>
                <ul className="list-disc pl-5 space-y-0.5 text-muted-foreground">{inc.guidance.map((g) => <li key={g}>{g}</li>)}</ul>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => copyText(d.summary_text, "Summary")} data-testid="wt-copy-summary"><Copy className="w-4 h-4 mr-1.5" /> Copy Summary</Button>
              <Button variant="outline" className="flex-1" onClick={() => copyText(d.technical_text, "Technical report")} data-testid="wt-copy-technical"><FileText className="w-4 h-4 mr-1.5" /> Copy Technical Report</Button>
            </div>
            <Collapsible>
              <CollapsibleTrigger className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="wt-incident-tech-toggle"><ChevronDown className="w-3.5 h-3.5" /> Technical Details</CollapsibleTrigger>
              <CollapsibleContent>
                <pre className="mt-2 text-[11px] leading-relaxed whitespace-pre-wrap break-all rounded-md bg-muted p-3" data-testid="wt-incident-tech-details">{[
                  `code: ${inc.what_failed}`, `severity: ${inc.severity}`, `source: ${inc.source}`, `alert_id: ${inc.alert_id}`, `system_id: ${inc.system_id}`,
                  `tenant_id: ${inc.tenant_id ?? "—"}`, `check_ref: ${inc.check_ref ?? "—"}`, `occurrences: ${inc.occurrences}`,
                  `detail: ${JSON.stringify(inc.detail)}`].join("\n")}</pre>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
